import { z } from 'zod';

// src/types/index.ts
var DieValueSchema = z.number().int().min(1).max(6);
var DiceSchema = z.array(DieValueSchema).length(5);
var HoldsSchema = z.array(z.boolean()).length(5);
var RollCountSchema = z.number().int().min(0).max(3);
var UpperCategorySchema = z.enum([
  "ones",
  "twos",
  "threes",
  "fours",
  "fives",
  "sixes"
]);
var LowerCategorySchema = z.enum([
  "max",
  "min",
  "straight",
  "fullHouse",
  "poker",
  "yamb"
]);
var CategorySchema = z.union([UpperCategorySchema, LowerCategorySchema]);
var ColumnTypeSchema = z.enum([
  "down",
  // Fill top to bottom
  "up",
  // Fill bottom to top
  "free",
  // Fill in any order
  "announce",
  // Announce category before 2nd roll
  "hand",
  // First roll only (iz ruke) - no re-rolls allowed
  "middle",
  // Fill from middle outward
  "call"
  // Announce before 1st roll (stricter than announce)
]);
var ColumnIdSchema = z.string();
var CellStateSchema = z.enum([
  "empty",
  "locked",
  "available",
  "filled"
]);
var GameStatusSchema = z.enum([
  "created",
  "waiting",
  "active",
  "finished",
  "archived"
]);

// src/scoring/index.ts
function countDice(dice) {
  const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  for (const die of dice) {
    counts[die] = (counts[die] ?? 0) + 1;
  }
  return counts;
}
function sumDice(dice) {
  return dice.reduce((sum, die) => sum + die, 0);
}
function hasStraight(dice) {
  const sorted = [...dice].sort((a, b) => a - b);
  const isLowStraight = sorted[0] === 1 && sorted[1] === 2 && sorted[2] === 3 && sorted[3] === 4 && sorted[4] === 5;
  const isHighStraight = sorted[0] === 2 && sorted[1] === 3 && sorted[2] === 4 && sorted[3] === 5 && sorted[4] === 6;
  return isLowStraight || isHighStraight;
}
function hasFullHouse(dice) {
  const counts = Object.values(countDice(dice));
  return counts.includes(3) && counts.includes(2);
}
function hasNOfAKind(dice, n) {
  const counts = Object.values(countDice(dice));
  return counts.some((count) => count >= n);
}
function getNOfAKindValue(dice, n) {
  const counts = countDice(dice);
  for (const [value, count] of Object.entries(counts)) {
    if (count >= n) {
      return parseInt(value, 10);
    }
  }
  return null;
}
var upperCategoryMap = {
  ones: 1,
  twos: 2,
  threes: 3,
  fours: 4,
  fives: 5,
  sixes: 6
};
function scoreUpperCategory(dice, category) {
  const targetValue = upperCategoryMap[category];
  return dice.filter((die) => die === targetValue).length * targetValue;
}
function scoreMax(dice) {
  return sumDice(dice);
}
function scoreMin(dice) {
  return sumDice(dice);
}
function scoreStraight(dice, config = {
  lowPoints: 35,
  highPoints: 45
}) {
  if (!hasStraight(dice)) {
    return 0;
  }
  const sorted = [...dice].sort((a, b) => a - b);
  return sorted[0] === 1 ? config.lowPoints : config.highPoints;
}
function scoreFullHouse(dice, bonusPoints = 30) {
  if (!hasFullHouse(dice)) {
    return 0;
  }
  return sumDice(dice) + bonusPoints;
}
function scorePoker(dice, bonusPoints = 40) {
  if (!hasNOfAKind(dice, 4)) {
    return 0;
  }
  return sumDice(dice) + bonusPoints;
}
function scoreYamb(dice, bonusPoints = 50) {
  if (!hasNOfAKind(dice, 5)) {
    return 0;
  }
  return sumDice(dice) + bonusPoints;
}
var lowerCategoryScorers = {
  max: scoreMax,
  min: scoreMin,
  straight: scoreStraight,
  fullHouse: scoreFullHouse,
  poker: scorePoker,
  yamb: scoreYamb
};
function calculateScore(dice, category) {
  if (category in upperCategoryMap) {
    return scoreUpperCategory(dice, category);
  }
  const scorer = lowerCategoryScorers[category];
  return scorer(dice);
}
function calculateAllScores(dice) {
  const categories = [
    "ones",
    "twos",
    "threes",
    "fours",
    "fives",
    "sixes",
    "max",
    "min",
    "straight",
    "fullHouse",
    "poker",
    "yamb"
  ];
  const scores = {};
  for (const category of categories) {
    scores[category] = calculateScore(dice, category);
  }
  return scores;
}
function calculateUpperBonus(upperScores, threshold = 60, bonusPoints = 30) {
  const sum = Object.values(upperScores).filter((score) => score !== null).reduce((total, score) => total + score, 0);
  return sum >= threshold ? bonusPoints : 0;
}
function calculateDifferenceBonus(maxScore, minScore, onesScore) {
  if (maxScore === null || minScore === null || onesScore === null) {
    return 0;
  }
  const onesCount = onesScore;
  return (maxScore - minScore) * onesCount;
}

// src/templates/index.ts
var CLASSIC_TEMPLATE = {
  id: "classic",
  name: "Classic Yamb",
  description: "Traditional 4-column Yamb with Down, Up, Free, and Announce columns",
  version: "1.0.0",
  categories: [
    "ones",
    "twos",
    "threes",
    "fours",
    "fives",
    "sixes",
    "max",
    "min",
    "straight",
    "fullHouse",
    "poker",
    "yamb"
  ],
  columns: [
    {
      id: "down",
      type: "down",
      name: "Down",
      description: "Fill categories from top to bottom"
    },
    {
      id: "up",
      type: "up",
      name: "Up",
      description: "Fill categories from bottom to top"
    },
    {
      id: "free",
      type: "free",
      name: "Free",
      description: "Fill categories in any order"
    },
    {
      id: "announce",
      type: "announce",
      name: "Announce",
      description: "Announce category before second roll"
    }
  ],
  scoring: {
    straightLowPoints: 35,
    straightHighPoints: 45,
    fullHouseBonus: 30,
    pokerBonus: 40,
    yambBonus: 50,
    upperBonusThreshold: 60,
    upperBonusPoints: 30
  },
  locale: {
    en: {
      ones: "Ones",
      twos: "Twos",
      threes: "Threes",
      fours: "Fours",
      fives: "Fives",
      sixes: "Sixes",
      max: "Max",
      min: "Min",
      straight: "Straight",
      fullHouse: "Full House",
      poker: "Poker",
      yamb: "Yamb"
    },
    sr: {
      ones: "Jedinice",
      twos: "Dvojke",
      threes: "Trojke",
      fours: "Cetvorke",
      fives: "Petice",
      sixes: "Sestice",
      max: "Maks",
      min: "Min",
      straight: "Kenta",
      fullHouse: "Ful",
      poker: "Poker",
      yamb: "Jamb"
    }
  }
};
var SOLO_TEMPLATE = {
  id: "solo",
  name: "Solo Yamb",
  description: "Single-column free-form game for practice",
  version: "1.0.0",
  categories: CLASSIC_TEMPLATE.categories,
  columns: [
    {
      id: "free",
      type: "free",
      name: "Free",
      description: "Fill categories in any order"
    }
  ],
  scoring: CLASSIC_TEMPLATE.scoring,
  locale: CLASSIC_TEMPLATE.locale
};
var FAST_TEMPLATE = {
  id: "fast",
  name: "Fast Yamb",
  description: "Two-column game for quick matches",
  version: "1.0.0",
  categories: CLASSIC_TEMPLATE.categories,
  columns: [
    {
      id: "down",
      type: "down",
      name: "Down",
      description: "Fill categories from top to bottom"
    },
    {
      id: "free",
      type: "free",
      name: "Free",
      description: "Fill categories in any order"
    }
  ],
  scoring: CLASSIC_TEMPLATE.scoring,
  locale: CLASSIC_TEMPLATE.locale
};
var KIDS_TEMPLATE = {
  id: "kids",
  name: "Kids Yamb",
  description: "Simplified game for younger players - just numbers and basic combos!",
  version: "1.0.0",
  categories: [
    "ones",
    "twos",
    "threes",
    "fours",
    "fives",
    "sixes",
    "max",
    "yamb"
  ],
  columns: [
    {
      id: "free",
      type: "free",
      name: "Free",
      description: "Fill any box in any order - no rules!"
    }
  ],
  scoring: {
    straightLowPoints: 0,
    straightHighPoints: 0,
    fullHouseBonus: 0,
    pokerBonus: 0,
    yambBonus: 30,
    // Smaller bonus for kids
    upperBonusThreshold: 50,
    // Easier threshold
    upperBonusPoints: 20
  },
  locale: {
    en: {
      ones: "\u2680 Ones",
      twos: "\u2681 Twos",
      threes: "\u2682 Threes",
      fours: "\u2683 Fours",
      fives: "\u2684 Fives",
      sixes: "\u2685 Sixes",
      max: "\u{1F53C} Max",
      yamb: "\u{1F3AF} Yamb!"
    },
    sr: {
      ones: "\u2680 Jedinice",
      twos: "\u2681 Dvojke",
      threes: "\u2682 Trojke",
      fours: "\u2683 \u010Cetvorke",
      fives: "\u2684 Petice",
      sixes: "\u2685 \u0160estice",
      max: "\u{1F53C} Maks",
      yamb: "\u{1F3AF} Jamb!"
    }
  }
};
var QUICK_TEMPLATE = {
  id: "quick",
  name: "Quick Game",
  description: "Super fast 5-minute game - upper section only",
  version: "1.0.0",
  categories: [
    "ones",
    "twos",
    "threes",
    "fours",
    "fives",
    "sixes"
  ],
  columns: [
    {
      id: "free",
      type: "free",
      name: "Free",
      description: "Fill categories in any order"
    }
  ],
  scoring: {
    straightLowPoints: 0,
    straightHighPoints: 0,
    fullHouseBonus: 0,
    pokerBonus: 0,
    yambBonus: 0,
    upperBonusThreshold: 60,
    upperBonusPoints: 30
  },
  locale: CLASSIC_TEMPLATE.locale
};
var CHALLENGE_TEMPLATE = {
  id: "challenge",
  name: "Challenge Mode",
  description: "Expert mode - complete columns in order with bonus multipliers",
  version: "1.0.0",
  categories: CLASSIC_TEMPLATE.categories,
  columns: [
    {
      id: "down",
      type: "down",
      name: "Down",
      description: "Fill from top to bottom"
    },
    {
      id: "up",
      type: "up",
      name: "Up",
      description: "Fill from bottom to top"
    },
    {
      id: "free",
      type: "free",
      name: "Free",
      description: "Fill in any order"
    },
    {
      id: "announce",
      type: "announce",
      name: "Announce",
      description: "Announce before second roll"
    }
  ],
  scoring: {
    straightLowPoints: 40,
    // Higher bonuses
    straightHighPoints: 50,
    fullHouseBonus: 35,
    pokerBonus: 45,
    yambBonus: 60,
    upperBonusThreshold: 63,
    // Harder threshold
    upperBonusPoints: 35
  },
  locale: CLASSIC_TEMPLATE.locale
};
var PRO_TEMPLATE = {
  id: "pro",
  name: "Pro Yamb",
  description: "Full 10-column traditional Yamb - the complete experience!",
  version: "1.0.0",
  categories: CLASSIC_TEMPLATE.categories,
  columns: [
    {
      id: "down1",
      type: "down",
      name: "\u2193",
      description: "Fill from top to bottom"
    },
    {
      id: "down2",
      type: "down",
      name: "\u2193\u2193",
      description: "Fill from top to bottom (second)"
    },
    {
      id: "up1",
      type: "up",
      name: "\u2191",
      description: "Fill from bottom to top"
    },
    {
      id: "up2",
      type: "up",
      name: "\u2191\u2191",
      description: "Fill from bottom to top (second)"
    },
    {
      id: "free",
      type: "free",
      name: "Free",
      description: "Fill in any order"
    },
    {
      id: "hand",
      type: "hand",
      name: "Hand",
      description: "First roll only - no re-rolls (iz ruke)"
    },
    {
      id: "middle",
      type: "middle",
      name: "Middle",
      description: "Fill from the middle outward (sredina)"
    },
    {
      id: "announce",
      type: "announce",
      name: "Announce",
      description: "Announce category before second roll (najava)"
    },
    {
      id: "call",
      type: "call",
      name: "Call",
      description: "Announce before first roll - highest risk/reward"
    },
    {
      id: "free2",
      type: "free",
      name: "Free 2",
      description: "Fill in any order (second free column)"
    }
  ],
  scoring: {
    straightLowPoints: 35,
    straightHighPoints: 45,
    fullHouseBonus: 30,
    pokerBonus: 40,
    yambBonus: 50,
    upperBonusThreshold: 60,
    upperBonusPoints: 30
  },
  locale: {
    en: {
      ...CLASSIC_TEMPLATE.locale.en,
      down1: "Down",
      down2: "Down 2",
      up1: "Up",
      up2: "Up 2",
      free: "Free",
      hand: "Hand",
      middle: "Middle",
      announce: "Announce",
      call: "Call",
      free2: "Free 2"
    },
    sr: {
      ...CLASSIC_TEMPLATE.locale.sr,
      down1: "Odozgo",
      down2: "Odozgo 2",
      up1: "Odozdo",
      up2: "Odozdo 2",
      free: "Slobodno",
      hand: "Iz Ruke",
      middle: "Sredina",
      announce: "Najava",
      call: "Dozivanje",
      free2: "Slobodno 2"
    }
  }
};
var TEMPLATES = {
  classic: CLASSIC_TEMPLATE,
  solo: SOLO_TEMPLATE,
  fast: FAST_TEMPLATE,
  kids: KIDS_TEMPLATE,
  quick: QUICK_TEMPLATE,
  challenge: CHALLENGE_TEMPLATE,
  pro: PRO_TEMPLATE
};
function getTemplate(id) {
  return TEMPLATES[id];
}
function getAllTemplates() {
  return Object.values(TEMPLATES);
}
function validateTemplate(template) {
  const validCategories = new Set(CLASSIC_TEMPLATE.categories);
  for (const cat of template.categories) {
    if (!validCategories.has(cat)) {
      return false;
    }
  }
  if (template.columns.length === 0) {
    return false;
  }
  const { scoring } = template;
  if (scoring.straightLowPoints < 0 || scoring.straightHighPoints < 0 || scoring.upperBonusThreshold < 0 || scoring.upperBonusPoints < 0) {
    return false;
  }
  return true;
}

// src/index.ts
var ALL_CATEGORIES = [
  "ones",
  "twos",
  "threes",
  "fours",
  "fives",
  "sixes",
  "max",
  "min",
  "straight",
  "fullHouse",
  "poker",
  "yamb"
];
function createEmptyCell() {
  return {
    state: "empty",
    score: null,
    entry: null
  };
}
function createColumn(id, type, categories) {
  const cells = {};
  for (const category of categories) {
    cells[category] = createEmptyCell();
  }
  return { id, type, cells };
}
function createSheet(template) {
  const columns = {};
  for (const colConfig of template.columns) {
    columns[colConfig.id] = createColumn(
      colConfig.id,
      colConfig.type,
      template.categories
    );
  }
  return {
    columns,
    upperBonus: 0,
    lowerBonus: 0,
    total: 0
  };
}
function createInitialTurn() {
  return {
    dice: [1, 1, 1, 1, 1],
    holds: [false, false, false, false, false],
    rollCount: 0,
    announcedCategory: null
  };
}
function rollDice(currentDice, holds, rngFn = Math.random) {
  return currentDice.map((die, index) => {
    if (holds[index]) {
      return die;
    }
    return Math.floor(rngFn() * 6) + 1;
  });
}
function toggleHold(holds, dieIndex) {
  if (dieIndex < 0 || dieIndex >= 5) {
    return holds;
  }
  const newHolds = [...holds];
  newHolds[dieIndex] = !newHolds[dieIndex];
  return newHolds;
}
function getCategoryOrder() {
  return ALL_CATEGORIES;
}
function getCategoryIndex(category) {
  return getCategoryOrder().indexOf(category);
}
function isCellAvailable(column, category, announcedCategory, rollCount = 3) {
  const cell = column.cells[category];
  if (cell.state === "filled" || cell.score !== null) {
    return false;
  }
  const categories = getCategoryOrder();
  switch (column.type) {
    case "down": {
      const categoryIdx = getCategoryIndex(category);
      for (let i = 0; i < categoryIdx; i++) {
        const cat = categories[i];
        if (cat !== void 0 && column.cells[cat]?.score === null) {
          return false;
        }
      }
      return true;
    }
    case "up": {
      const categoryIdx = getCategoryIndex(category);
      for (let i = categories.length - 1; i > categoryIdx; i--) {
        const cat = categories[i];
        if (cat !== void 0 && column.cells[cat]?.score === null) {
          return false;
        }
      }
      return true;
    }
    case "free":
      return true;
    case "announce":
      return category === announcedCategory;
    case "hand":
      return rollCount === 1;
    case "middle": {
      const categoryIdx = getCategoryIndex(category);
      const middleStart = 6;
      const middleEnd = 7;
      const maxCell = column.cells["max"];
      const minCell = column.cells["min"];
      const middleFilled = maxCell?.score !== null && minCell?.score !== null;
      if (categoryIdx === middleStart || categoryIdx === middleEnd) {
        return true;
      }
      if (!middleFilled) {
        return false;
      }
      if (categoryIdx < middleStart) {
        for (let i = middleStart - 1; i > categoryIdx; i--) {
          const cat = categories[i];
          if (cat !== void 0 && column.cells[cat]?.score === null) {
            return false;
          }
        }
        return true;
      }
      if (categoryIdx > middleEnd) {
        for (let i = middleEnd + 1; i < categoryIdx; i++) {
          const cat = categories[i];
          if (cat !== void 0 && column.cells[cat]?.score === null) {
            return false;
          }
        }
        return true;
      }
      return false;
    }
    case "call":
      return category === announcedCategory;
    default:
      return false;
  }
}
function getPotentialScores(dice, sheet, announcedCategory, rollCount = 3) {
  const scores = calculateAllScores(dice);
  const availableCells = [];
  for (const [columnId, column] of Object.entries(sheet.columns)) {
    for (const category of ALL_CATEGORIES) {
      if (isCellAvailable(column, category, announcedCategory, rollCount)) {
        availableCells.push({ columnId, category });
      }
    }
  }
  return { scores, availableCells };
}
function scoreCell(sheet, columnId, category, dice, announcedCategory, rollCount = 3) {
  const column = sheet.columns[columnId];
  if (!column) {
    return { success: false, newSheet: sheet, error: "Invalid column" };
  }
  if (!isCellAvailable(column, category, announcedCategory, rollCount)) {
    return { success: false, newSheet: sheet, error: "Cell not available" };
  }
  const score = calculateScore(dice, category);
  const newSheet = structuredClone(sheet);
  const newCell = newSheet.columns[columnId]?.cells[category];
  if (newCell) {
    newCell.state = "filled";
    newCell.score = score;
    newCell.entry = {
      category,
      columnId,
      score,
      dice: [...dice],
      timestamp: Date.now()
    };
  }
  recalculateTotals(newSheet);
  return { success: true, newSheet };
}
function recalculateTotals(sheet) {
  let total = 0;
  let upperSum = 0;
  const upperCategories = [
    "ones",
    "twos",
    "threes",
    "fours",
    "fives",
    "sixes"
  ];
  for (const column of Object.values(sheet.columns)) {
    for (const [category, cell] of Object.entries(column.cells)) {
      if (cell.score !== null) {
        total += cell.score;
        if (upperCategories.includes(category)) {
          upperSum += cell.score;
        }
      }
    }
  }
  sheet.upperBonus = upperSum >= 60 ? 30 : 0;
  total += sheet.upperBonus;
  sheet.total = total;
}
function createGame(templateId, playerIds) {
  const template = getTemplate(templateId);
  if (!template) {
    return null;
  }
  const players = playerIds.map((id, index) => ({
    id,
    seatOrder: index,
    sheet: createSheet(template),
    currentTurn: null,
    totalScore: 0
  }));
  return {
    id: crypto.randomUUID(),
    templateId,
    status: "created",
    currentPlayerId: playerIds[0] ?? null,
    turnIndex: 0,
    players,
    createdAt: Date.now(),
    finishedAt: null
  };
}
function processAction(state, action) {
  const player = state.players.find((p) => p.id === action.playerId);
  if (!player) {
    return { success: false, error: "Player not found" };
  }
  if (state.currentPlayerId !== action.playerId && action.type !== "ROLL") {
    return { success: false, error: "Not your turn" };
  }
  switch (action.type) {
    case "ROLL": {
      if (!player.currentTurn) {
        player.currentTurn = createInitialTurn();
      }
      if (player.currentTurn.rollCount >= 3) {
        return { success: false, error: "No rolls remaining" };
      }
      player.currentTurn.dice = rollDice(
        player.currentTurn.dice,
        player.currentTurn.holds
      );
      player.currentTurn.rollCount++;
      return { success: true, newState: { ...state } };
    }
    case "TOGGLE_HOLD": {
      if (!player.currentTurn || player.currentTurn.rollCount === 0) {
        return { success: false, error: "Must roll first" };
      }
      player.currentTurn.holds = toggleHold(
        player.currentTurn.holds,
        action.dieIndex
      );
      return { success: true, newState: { ...state } };
    }
    case "ANNOUNCE": {
      if (!player.currentTurn) {
        player.currentTurn = createInitialTurn();
      }
      const template = getTemplate(state.templateId);
      const hasCallColumn = template?.columns.some((c) => c.type === "call");
      if (hasCallColumn && player.currentTurn.rollCount > 0) ;
      if (player.currentTurn.rollCount > 1) {
        return {
          success: false,
          error: "Must announce before or after first roll"
        };
      }
      player.currentTurn.announcedCategory = action.category;
      return { success: true, newState: { ...state } };
    }
    case "SCORE": {
      if (!player.currentTurn || player.currentTurn.rollCount === 0) {
        return { success: false, error: "Must roll first" };
      }
      const result = scoreCell(
        player.sheet,
        action.columnId,
        action.category,
        player.currentTurn.dice,
        player.currentTurn.announcedCategory,
        player.currentTurn.rollCount
      );
      if (!result.success) {
        return { success: false, error: result.error ?? "Scoring failed" };
      }
      player.sheet = result.newSheet;
      player.totalScore = result.newSheet.total;
      player.currentTurn = null;
      const nextPlayerIndex = (state.players.findIndex((p) => p.id === action.playerId) + 1) % state.players.length;
      state.currentPlayerId = state.players[nextPlayerIndex]?.id ?? null;
      state.turnIndex++;
      const isFinished = isGameComplete(state);
      if (isFinished) {
        state.status = "finished";
        state.finishedAt = Date.now();
      }
      return { success: true, newState: { ...state } };
    }
    default:
      return { success: false, error: "Unknown action" };
  }
}
function isGameComplete(state) {
  for (const player of state.players) {
    for (const column of Object.values(player.sheet.columns)) {
      for (const cell of Object.values(column.cells)) {
        if (cell.score === null) {
          return false;
        }
      }
    }
  }
  return true;
}
function validateDice(dice) {
  if (!Array.isArray(dice) || dice.length !== 5) {
    return false;
  }
  return dice.every(
    (d) => typeof d === "number" && Number.isInteger(d) && d >= 1 && d <= 6
  );
}
function validateCategory(category) {
  return ALL_CATEGORIES.includes(category);
}

export { CHALLENGE_TEMPLATE, CLASSIC_TEMPLATE, CategorySchema, CellStateSchema, ColumnIdSchema, ColumnTypeSchema, DiceSchema, DieValueSchema, FAST_TEMPLATE, GameStatusSchema, HoldsSchema, KIDS_TEMPLATE, LowerCategorySchema, PRO_TEMPLATE, QUICK_TEMPLATE, RollCountSchema, SOLO_TEMPLATE, TEMPLATES, UpperCategorySchema, calculateAllScores, calculateDifferenceBonus, calculateScore, calculateUpperBonus, countDice, createGame, createInitialTurn, createSheet, getAllTemplates, getNOfAKindValue, getPotentialScores, getTemplate, hasFullHouse, hasNOfAKind, hasStraight, isCellAvailable, processAction, rollDice, scoreCell, scoreFullHouse, scoreMax, scoreMin, scorePoker, scoreStraight, scoreUpperCategory, scoreYamb, sumDice, toggleHold, validateCategory, validateDice, validateTemplate };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map